import {
  PolicyDeploymentScope,
  SwUpdateSource,
  UpdateImageFragment,
  useDeviceDetailsQuery,
  useEpUpdateImagesQuery,
  EnumVal,
  useProvisioningSyncMutation,
  ExtendedDeviceParameterFragment,
  Maybe,
  useHardwareModelWithAvailableSoftwareQuery,
  FeatureCategory,
  SoftwareSupportUpdatePath,
  SwSupportUpdatePathFragment
} from "generated/graphql";
import React, { ReactElement, useCallback, useEffect, useState } from "react";
import { LoadingIndicator } from "elements";
import { OptionType } from "elements/DropdownList";
import i18n, { LocalKeys, useTranslation } from "utilities/i18n";
import { useFormikContext } from "collections";
import { SettingsCategory } from "../Settings-Shared/DeviceSettingsComponents";
import { constructFilterList } from "utilities/filters/deviceFilters";
import {
  PolicyDetails,
  InheritanceDetails,
  getDeviceModel,
  SettingValue,
  SettingsValues,
  useProvisionedDevicePolicyScopeContext,
  constructCustomSwSupportUpdatePath
} from "./ProvisionedDeviceSettingsRouter";
import * as yup from "yup";
import { InferType } from "yup";
import { Layout } from "views/Main/Layouts";
import { FormSetting, SectionSetting } from "./ProvisionedDeviceSettings";
import {
  isPolyOS,
  isDefaultProvisionedDevice,
  isEagleEyeCube,
  isStudioUSB
} from "utilities/modelSelectors";
import { compareVersion } from "utilities/stringUtils";
import {
  AUTO_UPDATE_LATEST,
  CUSTOM_SUPPORT_UPDATE_PATH,
  CUSTOM_UPDATE_PATH,
  DEVICE_PARAM_TYPE,
  LATEST,
  MAIN,
  MIN_SOFTWARE_FOR_UTOPIA_OPTIONS,
  PARAM_TYPE,
  POLLING_INTERVAL,
  SCHEDULE_UPDATE_CHECKS,
  SOFTWARE_UPDATE,
  SUPPORT_UPDATE_PATH,
  UPDATE_PATH,
  UPDATE_SOURCE,
  UPDATE_START_TIME,
  UPDATE_STOP_TIME
} from "Constants";
import { useSelector } from "react-redux";
import { Guard } from "TsRoleGuards";
import { getSoftwareVersion } from "../List/DeviceCard";
import {
  RevertAction,
  useDiscardSetting,
  useRevertSetting
} from "./MySettings";
import { State } from "store";
import { FieldArray } from "formik";
import { isEmpty, isEqual } from "lodash";

const settingsNameMap: Record<string, string> = {
  [SCHEDULE_UPDATE_CHECKS]: "upgrade.auto.timeFrame.enable",
  [UPDATE_START_TIME]: "upgrade.auto.timeFrame.startTime",
  [UPDATE_STOP_TIME]: "upgrade.auto.timeFrame.stopTime",
  [POLLING_INTERVAL]: "upgrade.auto.polling.interval"
};
export const getDeviceSettingName = (name: string) => {
  return settingsNameMap[name] ?? name;
};

export const SW_UPDATE_POLICY_SETTINGS = [
  AUTO_UPDATE_LATEST,
  UPDATE_SOURCE,
  UPDATE_PATH,
  CUSTOM_UPDATE_PATH,
  CUSTOM_SUPPORT_UPDATE_PATH
];

type UpdateImage = UpdateImageFragment | null;

interface SettingsLoaderProps {
  model: string;
  deviceParameters: Maybe<ExtendedDeviceParameterFragment>[];
  children: (settings: Record<string, SectionSetting>) => ReactElement;
}

export interface ProvisionedUpdateSoftwareProps {
  id?: string;
  model: string;
  version?: string;
  settings: Record<string, ProvisionedUpdateSetting>;
  dirty: boolean;
}

type VersionOptions = Array<
  EnumVal & {
    swSupportUpdatePath: SwSupportUpdatePathFragment[];
  }
>;

type ProvisionedUpdateSetting = SectionSetting & {
  onChange?: {
    settingName: string;
    updater: (value: SettingValue) => SettingValue;
  }[];
};

export const generateTimeOptions = (): Array<OptionType> => {
  let options = [];
  for (let i = 0; i < 24; i++) {
    options.push({
      value: `${i < 10 ? "0" : ""}${i}:00`,
      text: `${i > 12 ? i - 12 : i === 0 ? 12 : i}:00 ${i > 11 ? "PM" : "AM"}`
    });
  }
  return options;
};

const convertToEnumVal = (options: Array<OptionType>): Array<EnumVal> =>
  options.map(option => ({
    value: option.value,
    friendlyValue: option.text,
    featureCategory: FeatureCategory.Released,
    __typename: "EnumVal"
  }));

const getFormSettingData = ({
  name,
  friendlyName,
  type,
  value,
  defaultValue = "",
  options,
  scope,
  model
}: {
  name: string;
  friendlyName: string;
  type: string;
  value: SettingValue;
  defaultValue?: string;
  options?: Array<EnumVal>;
  scope?: PolicyDeploymentScope | null;
  model: string;
}): SectionSetting => ({
  displayName: model,
  modelName: model,
  name,
  settingName: name,
  friendlyName,
  paramType: PARAM_TYPE.PARAM,
  type,
  value,
  defaultValue,
  policyDeploymentScope: scope || null,
  help: "",
  templates: "",
  callback: null,
  accessType: null,
  categories: [SOFTWARE_UPDATE, SOFTWARE_UPDATE],
  category: SOFTWARE_UPDATE,
  subcategory: SOFTWARE_UPDATE,
  grouping: null,
  groupingOrder: null,
  collection: MAIN,
  lensUserType: 1,
  lensConditional: null,
  lensConditionalValue: null,
  languageKey: "",
  min: 0,
  max: 0,
  enumVal: options || null,
  numReplace1Max: 0,
  numReplace2Max: 0,
  param2DArray: null,
  paramArray: null,
  featureCategory: FeatureCategory.Released,
  show: true,
  __typename: "DeviceParameterDTO"
});

const ProvisionedUpdateSoftwareLoader: React.FC<{
  policy: PolicyDetails;
  inheritance?: InheritanceDetails;
  deviceParameters: Maybe<ExtendedDeviceParameterFragment>[];
  children: (
    formValues: SettingsValues,
    settings: Record<string, ProvisionedUpdateSetting>
  ) => ReactElement;
}> = ({ policy, inheritance, deviceParameters, children }) => {
  const { t } = useTranslation();
  const deviceModel = getDeviceModel(policy.deviceModel ?? "");
  const isDeviceLevelPolicy =
    policy.policyDeploymentScope === PolicyDeploymentScope.Device;
  const isPolyOsDevice = isPolyOS(deviceModel);
  const isDefaultProvisioned = isDefaultProvisionedDevice(deviceModel);

  const { data, fetching } = useEpUpdateImagesQuery({
    variables: { deviceModel, isGlobal: true }
  });

  let images: Array<UpdateImage> = [];
  if (!fetching && data && data.getEpUpdateImages) {
    images = data!.getEpUpdateImages;
  } else if (fetching) {
    return <LoadingIndicator />;
  }

  const versionOptions: VersionOptions = images.map(image => {
    let updatePath = image?.directory || "";
    if (!isEmpty(image?.swSupportUpdatePath)) {
      const { swPath } =
        image?.swSupportUpdatePath?.find(
          updatePath => updatePath?.deviceModel === deviceModel
        ) ?? {};
      if (swPath) {
        updatePath += `/${swPath}`;
      }
    }

    return {
      friendlyValue: image?.imageVersion || "",
      value: updatePath,
      swSupportUpdatePath:
        image?.swSupportUpdatePath
          ?.filter(
            (updatePath): updatePath is SwSupportUpdatePathFragment =>
              !!updatePath && updatePath.deviceModel !== deviceModel
          )
          .map(({ deviceModel, swPath }) => ({
            __typename: "SoftwareSupportUpdatePath",
            deviceModel,
            swPath: `${image.directory}/${swPath}`
          })) || [],
      featureCategory: FeatureCategory.Released,
      __typename: "EnumVal"
    };
  });

  const { autoUpdateLatest, swUpdatePath, swSupportUpdatePath } = getUpdatePath(
    inheritance?.swUpdatePath?.value.toString(),
    policy.swUpdatePath,
    versionOptions
  );

  let customSwSupportUpdatePath:
    | SwSupportUpdatePathFragment[]
    | undefined = inheritance?.customSwSupportUpdatePath
    ?.value as SwSupportUpdatePathFragment[];
  if (isEmpty(customSwSupportUpdatePath)) {
    customSwSupportUpdatePath = policy.customSwSupportUpdatePath?.filter(
      (update): update is SoftwareSupportUpdatePath => !!update?.deviceModel
    );
  }
  if (isEmpty(customSwSupportUpdatePath)) {
    customSwSupportUpdatePath = constructCustomSwSupportUpdatePath(deviceModel);
  }

  let formValues: ProvisionedUpdateFormType = {
    [AUTO_UPDATE_LATEST]: autoUpdateLatest,
    [UPDATE_SOURCE]:
      ((inheritance?.swUpdateSource?.value as SwUpdateSource) ||
        policy.swUpdateSource) ??
      SwUpdateSource.StandardUrl,
    [UPDATE_PATH]: swUpdatePath,
    [SUPPORT_UPDATE_PATH]: swSupportUpdatePath,
    [CUSTOM_UPDATE_PATH]:
      (inheritance?.customSwUpdatePath?.value.toString() ||
        policy.customSwUpdatePath) ??
      "",
    [CUSTOM_SUPPORT_UPDATE_PATH]: customSwSupportUpdatePath ?? [],
    [POLLING_INTERVAL]: isDefaultProvisioned
      ? 86400
      : isDeviceLevelPolicy
      ? 300
      : 3600,
    [SCHEDULE_UPDATE_CHECKS]: false,
    [UPDATE_START_TIME]: "0:00",
    [UPDATE_STOP_TIME]: "0:00"
  };

  let swUpdatePolicySettings: Record<
    keyof Pick<
      ProvisionedUpdateFormType,
      | "autoUpdateLatest"
      | "swUpdateSource"
      | "swUpdatePath"
      | "customSwUpdatePath"
      | "customSwSupportUpdatePath"
    >,
    ProvisionedUpdateSetting
  > = {
    autoUpdateLatest: getFormSettingData({
      name: AUTO_UPDATE_LATEST,
      friendlyName: t("AUTO_UPDATE_LATEST"),
      type: DEVICE_PARAM_TYPE.BOOL,
      value: formValues[AUTO_UPDATE_LATEST],
      scope: inheritance?.swUpdatePath?.scope,
      model: deviceModel
    }),
    swUpdateSource: getFormSettingData({
      name: UPDATE_SOURCE,
      friendlyName: t("DOWNLOAD_SOURCE"),
      type: DEVICE_PARAM_TYPE.ENUM,
      options: updateSourceType,
      value: formValues[UPDATE_SOURCE],
      defaultValue: updateSourceType[0].friendlyValue ?? "",
      scope: inheritance?.swUpdateSource?.scope,
      model: deviceModel
    }),
    swUpdatePath: {
      ...getFormSettingData({
        name: UPDATE_PATH,
        friendlyName: t("SELECT_VERSION"),
        type: DEVICE_PARAM_TYPE.ENUM,
        options: versionOptions,
        value: formValues[UPDATE_PATH],
        scope: inheritance?.swUpdatePath?.scope,
        model: deviceModel
      }),
      onChange: [
        {
          settingName: SUPPORT_UPDATE_PATH,
          updater: (value: SettingValue) =>
            versionOptions.find(option => option.value === value)
              ?.swSupportUpdatePath || []
        }
      ]
    },
    customSwUpdatePath: getFormSettingData({
      name: CUSTOM_UPDATE_PATH,
      friendlyName: t("UPDATE_SERVER_ADDRESS"),
      type: DEVICE_PARAM_TYPE.STRING,
      value: formValues[CUSTOM_UPDATE_PATH],
      scope: inheritance?.customSwUpdatePath?.scope,
      model: deviceModel
    }),
    customSwSupportUpdatePath: getFormSettingData({
      name: CUSTOM_SUPPORT_UPDATE_PATH,
      friendlyName: t("ADDITIONAL_UPDATE_SERVER_ADDRESSES"),
      type: DEVICE_PARAM_TYPE.STRING,
      value: formValues[CUSTOM_SUPPORT_UPDATE_PATH],
      scope: inheritance?.customSwSupportUpdatePath?.scope,
      model: deviceModel
    })
  };

  return (
    <>
      {isPolyOsDevice || isDefaultProvisioned ? (
        <SettingsLoader model={deviceModel} deviceParameters={deviceParameters}>
          {settings => {
            const upgradePollingInterval = Number(
              settings[POLLING_INTERVAL]?.value
            );
            const upgradeTimeFrameEnable =
              settings[SCHEDULE_UPDATE_CHECKS]?.value === "True";
            const upgradeTimeFrameStart = settings[
              UPDATE_START_TIME
            ]?.value.toString();
            const upgradeTimeFrameStop = settings[
              UPDATE_STOP_TIME
            ]?.value.toString();

            formValues = {
              ...formValues,
              [SCHEDULE_UPDATE_CHECKS]: upgradeTimeFrameEnable,
              ...(upgradePollingInterval && {
                [POLLING_INTERVAL]: upgradePollingInterval
              }),
              ...(upgradeTimeFrameStart && {
                [UPDATE_START_TIME]: upgradeTimeFrameStart
              }),
              ...(upgradeTimeFrameStop && {
                [UPDATE_STOP_TIME]: upgradeTimeFrameStop
              })
            };

            return children(formValues, {
              ...swUpdatePolicySettings,
              ...settings
            });
          }}
        </SettingsLoader>
      ) : (
        children(formValues, swUpdatePolicySettings)
      )}
    </>
  );
};

const SettingsLoader: React.FC<SettingsLoaderProps> = ({
  model,
  deviceParameters,
  children
}) => {
  const isDefaultProvisioned = isDefaultProvisionedDevice(model);

  const AVAILABLE_SETTINGS = (isDefaultProvisioned
    ? [POLLING_INTERVAL]
    : [
        SCHEDULE_UPDATE_CHECKS,
        UPDATE_START_TIME,
        UPDATE_STOP_TIME,
        POLLING_INTERVAL
      ]
  ).map(name => getDeviceSettingName(name));

  let settings = {} as Record<string, SectionSetting>;
  if (deviceParameters) {
    settings = deviceParameters
      .filter(
        (param): param is ExtendedDeviceParameterFragment =>
          param !== null &&
          param.name !== null &&
          param.metadata !== null &&
          AVAILABLE_SETTINGS.includes(param.name)
      )
      .reduce((result, param) => {
        const settingName = Object.keys(settingsNameMap).find(
          key => settingsNameMap[key] === param.name
        )!;
        result[settingName] = {
          ...param.metadata!,
          settingName,
          category: SOFTWARE_UPDATE,
          subcategory: SOFTWARE_UPDATE,
          collection: MAIN,
          value: param.policyAttribute?.currentValue ?? "",
          policyDeploymentScope: param.policyDeploymentScope,
          show: true
        };
        return result;
      }, {} as Record<string, SectionSetting>);
  }

  return children(settings);
};

const updateSourceType: Array<EnumVal> = [
  {
    value: SwUpdateSource.StandardUrl,
    friendlyValue: i18n.t("POLY_LENS"),
    featureCategory: FeatureCategory.Released,
    __typename: "EnumVal"
  },
  {
    value: SwUpdateSource.CustomUrl,
    friendlyValue: i18n.t("CUSTOM_SERVER"),
    featureCategory: FeatureCategory.Released,
    __typename: "EnumVal"
  }
];

export let provisionedUpdateSchema = yup.object({
  [AUTO_UPDATE_LATEST]: yup.bool().required(),
  [UPDATE_SOURCE]: yup.mixed<SwUpdateSource>().required(),
  [UPDATE_PATH]: yup
    .string()
    .label(i18n.t("SELECT_VERSION"))
    .when([AUTO_UPDATE_LATEST, UPDATE_SOURCE], {
      is: (autoUpdateLatest, swUpdateSource) =>
        !autoUpdateLatest && swUpdateSource === SwUpdateSource.StandardUrl,
      then: yup.string().required()
    }),
  [SUPPORT_UPDATE_PATH]: yup.array().of(
    yup.object({
      deviceModel: yup.string().nullable(),
      swPath: yup.string().nullable()
    })
  ),
  [CUSTOM_UPDATE_PATH]: yup
    .string()
    .label(i18n.t("UPDATE_SERVER_ADDRESS"))
    .when([AUTO_UPDATE_LATEST, UPDATE_SOURCE], {
      is: (autoUpdateLatest, swUpdateSource) =>
        !autoUpdateLatest && swUpdateSource === SwUpdateSource.CustomUrl,
      then: yup.string().required()
    }),
  [CUSTOM_SUPPORT_UPDATE_PATH]: yup
    .array()
    .of(
      yup.object({
        deviceModel: yup.string().nullable(),
        swPath: yup.string().nullable()
      })
    )
    .label(i18n.t("ADDITIONAL_UPDATE_SERVER_ADDRESSES")),
  [POLLING_INTERVAL]: yup.number().label(i18n.t("UPGRADE_POLLING_INTERVAL")),
  [SCHEDULE_UPDATE_CHECKS]: yup.bool().label(i18n.t("SCHEDULE_UPDATE_CHECKS")),
  [UPDATE_START_TIME]: yup.string().label(i18n.t("FROM")),
  [UPDATE_STOP_TIME]: yup.string().label(i18n.t("TO"))
});

export type ProvisionedUpdateFormType = InferType<
  typeof provisionedUpdateSchema
>;

const getUpdatePath = (
  inheritedUpdatePath: string | undefined,
  policyUpdatePath: string | null,
  versionOptions: VersionOptions
) => {
  let swUpdatePath = (inheritedUpdatePath || policyUpdatePath) ?? "";
  const autoUpdateLatest = swUpdatePath === LATEST;
  if (swUpdatePath && !versionOptions.some(opt => opt.value === swUpdatePath)) {
    swUpdatePath = "";
  }
  const swSupportUpdatePath =
    versionOptions.find(option => option.value === swUpdatePath)
      ?.swSupportUpdatePath ?? [];

  return {
    swUpdatePath,
    swSupportUpdatePath,
    autoUpdateLatest
  };
};

export const ProvisionedUpdateSoftware: React.FC<ProvisionedUpdateSoftwareProps> = ({
  id,
  model,
  settings,
  version,
  dirty
}) => {
  const { t } = useTranslation();
  const deviceModel = getDeviceModel(model);
  const [{ policyScope }] = useProvisionedDevicePolicyScopeContext();
  const showInheritance = policyScope === PolicyDeploymentScope.Device;
  const { Content } = Layout;
  return (
    <Content.ScrollingList>
      <SettingsCategory title={t("SOFTWARE_UPDATE")}>
        <div className="details grid form mb-3">
          <div className="legend grid pr-0 pl-0 ml-0">
            {showInheritance && (
              <div className="col-3 offset-7">{t("INHERITANCE")}</div>
            )}
            <div className="col-2 pr-0">
              <UpdateNowButton
                id={id}
                model={deviceModel}
                version={version}
                dirty={dirty}
                className="text-right"
              />
            </div>
          </div>
          {id && <CurrentSoftwareVersion id={id} model={deviceModel} />}
          <ProvisionedUpdateSoftwareForm
            model={deviceModel}
            settings={settings}
          />
        </div>
      </SettingsCategory>
    </Content.ScrollingList>
  );
};

export const UpdateNowButton: React.FC<{
  id?: string;
  model: string;
  dirty: boolean;
  version?: string | undefined;
  className?: string;
}> = ({ id, model, dirty, className, version }) => {
  const { t } = useTranslation();
  const [, provisioningSync] = useProvisioningSyncMutation();
  const [syncing, setSyncing] = useState(false);

  const requestProvisioningSync = async () => {
    if (id) {
      setSyncing(true);
      await provisioningSync({ deviceId: id });
      setSyncing(false);
    }
  };

  const isEagleEye = isEagleEyeCube(model);
  const isStudioUSBDevice = isStudioUSB(model);
  const isMinStudioUSBVersion = version
    ? compareVersion(version, MIN_SOFTWARE_FOR_UTOPIA_OPTIONS) >= 0
    : false;

  const updateAllowed =
    !isEagleEye && (!isStudioUSBDevice || isMinStudioUSBVersion);

  return (
    <Guard requirement="update:device">
      {id && updateAllowed && (
        <div className={className ?? ""}>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="update-button"
            disabled={syncing || dirty}
            onClick={requestProvisioningSync}
          >
            {syncing ? <span>{t("UPDATING")}</span> : t("UPDATE_NOW")}
          </button>
        </div>
      )}
    </Guard>
  );
};

export const ProvisionedUpdateSoftwareForm: React.FC<{
  model: string;
  settings: Record<string, ProvisionedUpdateSetting>;
  revertAction?: RevertAction;
}> = ({ model, settings, revertAction }) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<ProvisionedUpdateFormType>();
  const changedSettings = useSelector((s: State) => s.settings);
  const discardChange = useDiscardSetting();
  const [{ policyScope }] = useProvisionedDevicePolicyScopeContext();

  const timeOptions = convertToEnumVal(generateTimeOptions());

  const pollingIntervalMetadata = settings?.[POLLING_INTERVAL];
  if (pollingIntervalMetadata) {
    const min = pollingIntervalMetadata.min ?? 0;
    const max = pollingIntervalMetadata.max ?? Infinity;
    provisionedUpdateSchema = provisionedUpdateSchema.shape({
      [POLLING_INTERVAL]: yup
        .number()
        .min(min, t("FORM_VALIDATE_RANGE_UNDERFLOW", { min }))
        .max(max, t("FORM_VALIDATE_RANGE_OVERFLOW", { max }))
        .label("UPGRADE_POLLING_INTERVAL")
    });
  }

  const discardSettings = useCallback(
    (settingsToDiscard: string[]) => {
      settingsToDiscard.forEach(settingName => {
        const setting = settings[settingName];
        if (setting) {
          const { category, subcategory, collection, settingName } = setting;
          const changed =
            changedSettings?.[category]?.[subcategory]?.[collection]?.[
              settingName
            ];
          if (changed === "edited") {
            discardChange(category, subcategory, collection, settingName);
          }
        }
      });
    },
    [settings, changedSettings, discardChange]
  );

  const showUpdateWindowSettings = values[SCHEDULE_UPDATE_CHECKS];
  const isUpdateStartReverted =
    changedSettings?.[SOFTWARE_UPDATE]?.[SOFTWARE_UPDATE]?.[MAIN]?.[
      UPDATE_START_TIME
    ] === "reverted";
  const isUpdateStopReverted =
    changedSettings?.[SOFTWARE_UPDATE]?.[SOFTWARE_UPDATE]?.[MAIN]?.[
      UPDATE_STOP_TIME
    ] === "reverted";

  useEffect(() => {
    // when autoUpdateLatest is turned on, discard changes to swUpdateSource, swUpdatePath and customSwUpdatePath (if any)
    if (values.autoUpdateLatest === true) {
      discardSettings([
        UPDATE_SOURCE,
        UPDATE_PATH,
        SUPPORT_UPDATE_PATH,
        CUSTOM_UPDATE_PATH,
        CUSTOM_SUPPORT_UPDATE_PATH
      ]);
    }

    // when swUpdateSource is set to Poly Lens discard changes to customSwUpdatePath
    // when its set to Custom Server discard changes to swUpdatePath
    if (values.swUpdateSource === SwUpdateSource.StandardUrl) {
      discardSettings([CUSTOM_UPDATE_PATH, CUSTOM_SUPPORT_UPDATE_PATH]);
    } else if (values.swUpdateSource === SwUpdateSource.CustomUrl) {
      discardSettings([UPDATE_PATH, SUPPORT_UPDATE_PATH]);
    }

    // when Update Window is turned off discard changes to From/To settings
    if (values.upgradeTimeFrameEnable === false) {
      discardSettings([UPDATE_START_TIME, UPDATE_STOP_TIME]);
    }
  }, [
    values.autoUpdateLatest,
    values.swUpdateSource,
    values.upgradeTimeFrameEnable,
    discardSettings
  ]);

  // SW Update policy settings must be reverted together
  // if customSwSupportUpdatePath is not applicable to this model it should be filtered out
  const swUpdatePolicySettings = SW_UPDATE_POLICY_SETTINGS.filter(
    setting =>
      setting !== CUSTOM_SUPPORT_UPDATE_PATH ||
      !isEmpty(values[CUSTOM_SUPPORT_UPDATE_PATH])
  ).map(settingName => settings[settingName]);

  const showDividers =
    !!revertAction && swUpdatePolicySettings.some(setting => !!setting);

  return (
    <>
      {showDividers && <hr />}
      <SoftwareUpdateSetting
        name={AUTO_UPDATE_LATEST}
        setting={settings[AUTO_UPDATE_LATEST]}
        revert={{
          // SW Update policy on Device Model level shouldn't be reverted/deleted, that policy should always exist
          revertable: policyScope !== PolicyDeploymentScope.DeviceModel,
          revertGroup: swUpdatePolicySettings,
          discardableIfReverted: true,
          disableIfReverted: true,
          action: revertAction
        }}
      />
      {values.autoUpdateLatest && <LatestSoftwareVersion model={model} />}
      <div
        className={`show-hide ${!values.autoUpdateLatest ? "show" : "hide"}`}
        data-testid="updateVersionControls"
      >
        <SoftwareUpdateSetting
          name={UPDATE_SOURCE}
          setting={settings[UPDATE_SOURCE]}
          revert={{
            revertable: false,
            disableIfReverted: true,
            action: revertAction
          }}
        />
        <div
          className={`show-hide ${
            values.swUpdateSource === SwUpdateSource.StandardUrl ||
            !values.swUpdateSource
              ? "show"
              : "hide"
          }`}
          data-testid="updatePath"
        >
          <SoftwareUpdateSetting
            name={UPDATE_PATH}
            setting={settings[UPDATE_PATH]}
            revert={{
              revertable: false,
              disableIfReverted: true,
              action: revertAction
            }}
          />
        </div>
        <div
          className={`show-hide ${
            values.swUpdateSource === SwUpdateSource.CustomUrl ||
            !values.swUpdateSource
              ? "show"
              : "hide"
          }`}
          data-testid="customUpdatePath"
        >
          <SoftwareUpdateSetting
            name={CUSTOM_UPDATE_PATH}
            setting={settings[CUSTOM_UPDATE_PATH]}
            revert={{
              revertable: false,
              disableIfReverted: true,
              action: revertAction
            }}
          />
          {settings[CUSTOM_SUPPORT_UPDATE_PATH] &&
            !isEmpty(values[CUSTOM_SUPPORT_UPDATE_PATH]) && (
              <>
                <SoftwareUpdateSetting
                  name={CUSTOM_SUPPORT_UPDATE_PATH}
                  setting={{ ...settings[CUSTOM_SUPPORT_UPDATE_PATH] }}
                  hasInput={false}
                  revert={{
                    revertable: false,
                    disableIfReverted: true,
                    action: revertAction
                  }}
                />
                <FieldArray
                  name={CUSTOM_SUPPORT_UPDATE_PATH}
                  render={() =>
                    values[CUSTOM_SUPPORT_UPDATE_PATH].map((value, index) => (
                      <SoftwareUpdateSetting
                        key={`${CUSTOM_SUPPORT_UPDATE_PATH}.${index}`}
                        name={`${CUSTOM_SUPPORT_UPDATE_PATH}.${index}.swPath`}
                        setting={{
                          ...settings[CUSTOM_SUPPORT_UPDATE_PATH],
                          friendlyName: value.deviceModel,
                          value: value.swPath || ""
                        }}
                        simpleSetting
                        revert={{
                          revertable: false,
                          disableIfReverted: true,
                          action: revertAction
                        }}
                      />
                    ))
                  }
                />
              </>
            )}
        </div>
      </div>
      {showDividers && <hr />}
      <div data-testid="upgrade-schedule-form">
        <SoftwareUpdateSetting
          name={POLLING_INTERVAL}
          setting={
            settings[POLLING_INTERVAL] && {
              ...settings[POLLING_INTERVAL],
              friendlyName: t("UPGRADE_POLLING_INTERVAL")
            }
          }
          revert={{
            revertable: true,
            discardableIfReverted: true,
            action: revertAction
          }}
        />
        <SoftwareUpdateSetting
          name={SCHEDULE_UPDATE_CHECKS}
          setting={
            settings[SCHEDULE_UPDATE_CHECKS] && {
              ...settings[SCHEDULE_UPDATE_CHECKS],
              friendlyName: t("SCHEDULE_UPDATE_CHECKS")
            }
          }
          revert={{
            revertable: true,
            discardableIfReverted: true,
            action: revertAction
          }}
        />
        <div
          className={`show-hide ${
            showUpdateWindowSettings || isUpdateStartReverted ? "show" : "hide"
          }`}
          data-testid="upgrade-time-form"
        >
          <SoftwareUpdateSetting
            name={UPDATE_START_TIME}
            setting={
              settings[UPDATE_START_TIME] && {
                ...settings[UPDATE_START_TIME],
                friendlyName: t("FROM"),
                type: DEVICE_PARAM_TYPE.ENUM,
                enumVal: timeOptions
              }
            }
            revert={{
              revertable: true,
              discardableIfReverted: true,
              action: revertAction
            }}
          />
        </div>
        <div
          className={`show-hide ${
            showUpdateWindowSettings || isUpdateStopReverted ? "show" : "hide"
          }`}
          data-testid="upgrade-time-form"
        >
          <SoftwareUpdateSetting
            name={UPDATE_STOP_TIME}
            setting={
              settings[UPDATE_STOP_TIME] && {
                ...settings[UPDATE_STOP_TIME],
                friendlyName: t("TO"),
                type: DEVICE_PARAM_TYPE.ENUM,
                enumVal: timeOptions
              }
            }
            revert={{
              revertable: true,
              discardableIfReverted: true,
              action: revertAction
            }}
          />
        </div>
      </div>
    </>
  );
};

const SoftwareUpdateSetting: React.FC<{
  name: string;
  setting?: ProvisionedUpdateSetting;
  hasInput?: boolean;
  simpleSetting?: boolean;
  revert: {
    revertable: boolean;
    revertGroup?: ProvisionedUpdateSetting[];
    disableIfReverted?: boolean;
    discardableIfReverted?: boolean;
    action?: RevertAction;
  };
}> = ({ name, setting, revert, hasInput = true, simpleSetting = false }) => {
  const discardChange = useDiscardSetting();
  const revertSetting = useRevertSetting();
  const [{ policyScope }] = useProvisionedDevicePolicyScopeContext();

  const { getFieldHelpers, getFieldMeta } = useFormikContext<
    ProvisionedUpdateFormType
  >();
  const { value } = getFieldMeta(name);

  const changedSettings = useSelector((s: State) => s.settings);

  useEffect(() => {
    // if there is onChange property, go through it and update each of the dependent settings when this setting is changed
    if (setting?.onChange) {
      setting.onChange.forEach(({ settingName, updater }) => {
        const { setValue } = getFieldHelpers(settingName);
        const { value: settingValue } = getFieldMeta(settingName);
        const newValue = updater(value as SettingValue);
        if (!isEqual(settingValue, newValue)) {
          setValue(newValue);
        }
      });
    }
  }, [setting, value, getFieldHelpers, getFieldMeta]);

  if (!setting) {
    return null;
  }

  const {
    revertable,
    revertGroup,
    disableIfReverted,
    discardableIfReverted,
    action: revertAction
  } = revert;

  const handleRevert = (
    revertFn: (
      category: string,
      subcategory: string,
      collection: string,
      settingName: string,
      type?: string
    ) => void
  ) => {
    const settingsToRevert = revertGroup?.filter(setting => !!setting) ?? [
      setting
    ];
    settingsToRevert.forEach(
      ({ category, subcategory, collection, settingName, type }) => {
        revertFn(category, subcategory, collection, settingName, type ?? "");
      }
    );
  };

  const { category, subcategory, collection, settingName } = setting;
  const change =
    changedSettings?.[category]?.[subcategory]?.[collection]?.[settingName];
  const isReverted = change === "reverted";
  let onRevert: (() => void) | undefined;
  let revertIcon: string | undefined;
  let iconClassName: string | undefined;
  if (revertAction === RevertAction.REVERT && revertable) {
    onRevert =
      policyScope === setting?.policyDeploymentScope
        ? () => handleRevert(revertSetting)
        : undefined;
  } else if (
    revertAction === RevertAction.DISCARD &&
    (!isReverted || discardableIfReverted)
  ) {
    onRevert = () => handleRevert(discardChange);
    revertIcon = "clear_circle_outline";
    iconClassName = "outline";
  }

  const disabled = isReverted && disableIfReverted;

  return (
    <FormSetting
      name={name}
      setting={setting}
      hasInput={hasInput}
      simpleSetting={simpleSetting}
      revert={{
        action: onRevert,
        icon: revertIcon,
        iconClassName
      }}
      disabled={disabled}
    />
  );
};

const CurrentSoftwareVersion: React.FC<{ id: string; model: string }> = ({
  id,
  model
}) => {
  const { data } = useDeviceDetailsQuery({
    variables: {
      filter: {
        AND: constructFilterList({ id })
      }
    }
  });
  let currentVersion = "";
  if (data && data?.devices?.length > 0) {
    const { softwareVersion, softwareBuild } = data.devices[0];
    currentVersion = `${getSoftwareVersion(
      softwareVersion,
      softwareBuild,
      model
    )}`;
  } else {
    return null;
  }

  return (
    <SoftwareVersionField
      softwareVersion={currentVersion}
      label={"CURRENT_VERSION"}
    />
  );
};

export const LatestSoftwareVersion: React.FC<{ model: string }> = ({
  model
}) => {
  const { data } = useHardwareModelWithAvailableSoftwareQuery({
    variables: {
      id: model
    }
  });
  let latestVersion = "";
  if (data && data?.hardwareModel?.hardwareProducts) {
    // find first product with an available software release
    const availableSoftware = data.hardwareModel.hardwareProducts.find(
      product => product.availableProductSoftware != null
    )?.availableProductSoftware;
    if (availableSoftware) {
      if (availableSoftware.productBuild && isPolyOS(model)) {
        latestVersion =
          availableSoftware.productBuild.version +
          "." +
          availableSoftware.productBuild.build;
      } else {
        latestVersion = availableSoftware.version;
      }
    }
  } else {
    return null;
  }

  return (
    <SoftwareVersionField
      softwareVersion={latestVersion}
      label={"LATEST_VERSION"}
      className="latest-version"
    />
  );
};

export const SoftwareVersionField: React.FC<{
  softwareVersion: string;
  label: LocalKeys;
  className?: string;
}> = ({ softwareVersion, label, className }) => {
  const { t } = useTranslation();
  return (
    <div className="form-group">
      <div className="col-4 text-right">
        <label>{t(label)}:</label>
      </div>
      <div className={`col-4 ${className}`}>{softwareVersion}</div>
    </div>
  );
};

export default ProvisionedUpdateSoftwareLoader;
